import { createVariantMatcher } from 'variant-media-matcher';

const DEBUG_STORAGE_KEY = 'media-filter-debug';

/**
 * Diagnostics are on in the theme editor and on any unpublished theme, so nothing has to be switched on
 * during development. localStorage.setItem('media-filter-debug', '1') turns them on elsewhere; a URL
 * parameter was tried first but is lost on the next navigation.
 */
function debugEnabled() {
  const shopify = window.Shopify;
  if (shopify?.designMode) return true;
  if (shopify?.theme?.role && shopify.theme.role !== 'main') return true;

  try {
    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === '1';
  } catch {
    // Reading storage throws when the browser blocks site data
    return false;
  }
}

/**
 * Liquid renders the preview file name onto the media element itself, so every media type is read the
 * same way — including 3D models, which render no <img> at all. The <img> is only a fallback for markup
 * rendered before that attribute existed.
 */
function readMedia(item) {
  const previewFile = item.getAttribute('data-media-file') || item.querySelector('img')?.src;
  if (!previewFile) return null;

  return { filename: previewFile.split('/').pop().split('?')[0].toLowerCase() };
}

/** Collects what the matcher could not decide cleanly and prints it once per filter run. */
class MediaFilterDiagnostics {
  constructor(enabled) {
    this.enabled = enabled;
    this.issues = [];
  }

  record(media, report) {
    if (!this.enabled) return;

    // Nested values like "70 g" inside "12 x 70 g" are exactly what emptied the 12-pack gallery
    report.options
      .filter(({ candidates }) => candidates.length > 1)
      .forEach(({ option, candidates, value }) => {
        this.issues.push({
          file: media.filename,
          issue: `option "${option}": several values matched, picked the most specific one`,
          detail: `${candidates.join(' / ')} → ${value}`,
        });
      });

    // A file name that names no option at all shows on every variant. Correct for a lifestyle shot,
    // a naming mistake for a product shot — worth one line so the difference can be checked.
    if (report.options.every(({ value }) => value === null)) {
      this.issues.push({
        file: media.filename,
        issue: 'file name names no option value, media shows on every variant',
        detail: report.options.map(({ option }) => option).join(', '),
      });
    }
  }

  flush() {
    if (!this.enabled || this.issues.length === 0) return;

    console.groupCollapsed(`variant-media-filter: ${this.issues.length} data issue(s) in this gallery`);
    console.table(this.issues);
    console.groupEnd();
  }
}

/**
 * Shows only the gallery media that belong to the selected variant.
 *
 * Owns the hidden state of the gallery: it writes the hidden attribute on media, thumbnails and page
 * dots, and keeps the filtered-indexes attribute in sync so anything reading the gallery sees one state.
 */
class VariantMediaFilter extends HTMLElement {
  connectedCallback() {
    this.debug = debugEnabled();
    this.frame = null;
    this.lastApplied = null;

    this.gallery = this.closest('product-gallery');
    this.formId = this.gallery?.getAttribute('form') ?? null;
    this.productForm = this.formId ? document.getElementById(this.formId) : null;

    // Kept on the instance so disconnectedCallback can hand the same references back: removeEventListener
    // compares by identity, and a freshly written arrow function would not match the one that was added.
    this.variantChangeHandler = (event) => this.onVariantChange(event);
    this.rerenderHandler = () => this.onRerender();

    this.productForm?.addEventListener('variant:change', this.variantChangeHandler);
    this.productForm?.addEventListener('product:rerender', this.rerenderHandler);

    this.runInitialFilter();
  }

  // Removes the listeners this element left on the product form.
  disconnectedCallback() {
    this.productForm?.removeEventListener('variant:change', this.variantChangeHandler);
    this.productForm?.removeEventListener('product:rerender', this.rerenderHandler);

    // A frame scheduled just before removal would otherwise run against a gallery nobody shows
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }

  /**
   * The single way a filter run is started.
   *
   * Deferring to a frame lets the carousel finish initialising and iOS finish restoring scroll, and it
   * coalesces work: the theme fires product:rerender and variant:change back to back on one variant
   * change, so without cancelling the pending frame the gallery would be filtered twice — once with the
   * previous variant and once with the new one.
   */
  scheduleFilter(optionData, variant) {
    this.lastApplied = { optionData, variant };

    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.filterMedia(optionData, variant);
    });
  }

  /** Parsed once: the values never change between the variants of a product, only the selection does. */
  get optionSchema() {
    if (this.schema) return this.schema;

    const script = this.querySelector('script[type="application/json"]');
    if (!script) {
      console.warn('variant-media-filter: option data is missing, filtering is off');
      this.schema = [];
      return this.schema;
    }

    try {
      this.schema = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('variant-media-filter: option data is not valid JSON, filtering is off', error);
      this.schema = [];
    }

    return this.schema;
  }

  runInitialFilter() {
    if (!this.formId) return;
    const picker = document.querySelector(`variant-picker[form-id="${this.formId}"]`);
    if (!picker?.selectedVariant) return;

    this.scheduleFilter(this.buildOptionData(picker.selectedVariant), picker.selectedVariant);
  }

  onVariantChange(event) {
    const { variant } = event.detail;
    if (!variant) return;

    this.scheduleFilter(this.buildOptionData(variant), variant);
  }

  /**
   * The theme rewrites hidden on every media item when the section rerenders, so the last applied state
   * is scheduled again. When a variant change caused the rerender, the variant:change that follows
   * reschedules with the new variant and this run never reaches the DOM.
   */
  onRerender() {
    if (!this.lastApplied) return;

    this.scheduleFilter(this.lastApplied.optionData, this.lastApplied.variant);
  }

  buildOptionData(variant) {
    return this.optionSchema.reduce((data, option, index) => {
      data[option.name.toLowerCase()] = {
        // option1..option3 rather than variant.options: the flat keys are the ones the theme's own JSON
        // is guaranteed to carry, and a missing selection would hide every media of the variant
        selected: variant[`option${index + 1}`]?.toLowerCase(),
        all: option.values.map((value) => value.toLowerCase()),
      };
      return data;
    }, {});
  }

  filterMedia(optionData, variant) {
    if (!this.gallery) return;

    const mediaItems = Array.from(this.gallery.querySelectorAll('.product-gallery__media'));
    if (mediaItems.length === 0) return;

    const matcher = createVariantMatcher(optionData);
    const diagnostics = new MediaFilterDiagnostics(this.debug);

    const featuredMediaId = variant?.featured_media?.id?.toString() ?? null;
    const featuredIndex = featuredMediaId
      ? mediaItems.findIndex((item) => item.getAttribute('data-media-id') === featuredMediaId)
      : -1;

    // Resolve first — a result that would empty the gallery has to be discarded, not applied
    const visibility = mediaItems.map((item, index) => {
      // The variant's own media must stay reachable: the carousel scrolls to it on every change
      if (index === featuredIndex) return true;

      const media = readMedia(item);
      if (!media) return true;

      const report = matcher.evaluate(media);
      diagnostics.record(media, report);

      return report.visible;
    });

    if (!visibility.includes(true)) {
      console.warn('variant-media-filter: no media matched the variant, showing the full gallery', optionData);
      visibility.fill(true);
    }

    diagnostics.flush();

    this.applyVisibility(mediaItems, visibility);
    this.focusMedia(visibility, featuredIndex);
  }

  applyVisibility(mediaItems, visibility) {
    // Media, thumbnail and page dot share the media id. Grouping them in one pass keeps this linear:
    // querying per item would walk the whole gallery as many times as there are media.
    const relatedElements = new Map();
    for (const element of this.gallery.querySelectorAll('[data-media-id]')) {
      const mediaId = element.getAttribute('data-media-id');
      const group = relatedElements.get(mediaId);
      if (group) group.push(element);
      else relatedElements.set(mediaId, [element]);
    }

    const hiddenPositions = [];

    mediaItems.forEach((item, index) => {
      const hidden = !visibility[index];
      const group = relatedElements.get(item.getAttribute('data-media-id')) ?? [item];

      group.forEach((element) => element.toggleAttribute('hidden', hidden));
      if (hidden) hiddenPositions.push(index + 1);
    });

    // Positions count from one: the theme subtracts one when it reads this attribute back
    this.gallery.setAttribute('filtered-indexes', JSON.stringify(hiddenPositions));
  }

  /**
   * The theme scrolls to the variant's featured media on the same event and this runs after it, so
   * landing on the first visible media would undo that. The featured one wins whenever it survived.
   */
  focusMedia(visibility, featuredIndex) {
    const carousel = this.gallery.querySelector('media-carousel');
    if (!carousel) return;

    const firstVisible = visibility.indexOf(true);
    const target = featuredIndex !== -1 && visibility[featuredIndex] ? featuredIndex : firstVisible;

    carousel.select(target, { animate: false });
    if (target !== firstVisible) return;

    // iOS: scrollTo() may be overridden by the browser — reset scrollLeft directly after paint
    requestAnimationFrame(() => {
      carousel.scrollLeft = 0;
      // The thumbnail list only renders for part of the section settings
      const thumbnailList = this.gallery.querySelector('.product-gallery__thumbnail-list');
      if (thumbnailList) thumbnailList.scrollLeft = 0;
    });
  }
}

customElements.define('variant-media-filter', VariantMediaFilter);
